import React, { useState, useEffect } from 'react';
import { Bell, Send, Users, Clock, Loader2 } from 'lucide-react';
import { notificationsAPI } from '../../services/api';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';

const AdminNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({
    title: '',
    message: '',
    target_tiers: ['all']
  });

  const tiers = ['all', 'Bronze', 'Silver', 'Gold', 'Platinum'];

  useEffect(() => { fetchNotifications(); }, []);

  const fetchNotifications = async () => {
    try {
      const response = await notificationsAPI.getAll();
      setNotifications(response.data.notifications || []);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTierToggle = (tier) => {
    setForm(prev => {
      let tiers = [...prev.target_tiers];
      if (tier === 'all') {
        tiers = tiers.includes('all') ? [] : ['all'];
      } else {
        tiers = tiers.filter(t => t !== 'all');
        if (tiers.includes(tier)) {
          tiers = tiers.filter(t => t !== tier);
        } else {
          tiers.push(tier);
        }
      }
      return { ...prev, target_tiers: tiers.length > 0 ? tiers : ['all'] };
    });
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!form.title || !form.message) {
      toast.error('Please fill in all fields');
      return;
    }
    setSending(true);
    try {
      const response = await notificationsAPI.create(form);
      toast.success(`Notification sent to ${response.data.recipients_count} customers`);
      fetchNotifications();
      setForm({ title: '', message: '', target_tiers: ['all'] });
    } catch (error) {
      toast.error('Failed to send notification');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6 pt-12 lg:pt-0 animate-fadeIn">
      <div>
        <h1 className="font-serif text-3xl text-[#4A154B]">Notifications</h1>
        <p className="text-gray-600">Send announcements to customers</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Send Notification */}
        <div className="glass rounded-2xl p-6">
          <h3 className="font-serif text-xl text-[#4A154B] mb-4 flex items-center gap-2">
            <Send className="w-5 h-5 text-[#C9A689]" />
            Send Notification
          </h3>
          <form onSubmit={handleSend} className="space-y-4">
            <div>
              <label className="form-label">Title *</label>
              <Input
                value={form.title}
                onChange={(e) => setForm({...form, title: e.target.value})}
                placeholder="Special Announcement"
                data-testid="notification-title-input"
              />
            </div>
            <div>
              <label className="form-label">Message *</label>
              <Textarea
                value={form.message}
                onChange={(e) => setForm({...form, message: e.target.value})}
                placeholder="Your message to customers..."
                rows={4}
                data-testid="notification-message-input"
              />
            </div>
            <div>
              <label className="form-label">Target Audience</label>
              <div className="flex flex-wrap gap-3 mt-2">
                {tiers.map(tier => (
                  <label key={tier} className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={form.target_tiers.includes(tier)}
                      onCheckedChange={() => handleTierToggle(tier)}
                    />
                    <span className="text-sm capitalize">{tier === 'all' ? 'All Customers' : tier}</span>
                  </label>
                ))}
              </div>
            </div>
            <Button type="submit" disabled={sending} className="w-full btn-primary" data-testid="send-notification-btn">
              {sending ? (
                <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Sending...</>
              ) : (
                <><Send className="w-4 h-4 mr-2" /> Send Notification</>
              )}
            </Button>
          </form>

          {/* Quick Templates */}
          <div className="mt-6 pt-6 border-t border-[#F5E6D3]">
            <p className="text-sm text-gray-500 mb-3">Quick Templates</p>
            <div className="flex flex-wrap gap-2">
              {[
                { title: '2x Points Weekend!', message: 'Earn double points on all services this weekend only!' },
                { title: 'New Services Available', message: 'Check out our latest treatments and services at Salon O.' },
                { title: 'Holiday Hours', message: 'We will be open special hours during the holiday season.' },
              ].map((template, i) => (
                <button
                  key={i}
                  onClick={() => setForm({ ...form, title: template.title, message: template.message })}
                  className="px-3 py-1.5 bg-[#F5E6D3] rounded-full text-sm text-[#4A154B] hover:bg-[#C9A689] hover:text-white transition-colors"
                >
                  {template.title}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Sent History */}
        <div className="glass rounded-2xl p-6">
          <h3 className="font-serif text-xl text-[#4A154B] mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-[#C9A689]" />
            Sent History
          </h3>
          
          {loading ? (
            <div className="space-y-3">
              {[1,2,3].map(i => <div key={i} className="skeleton h-24 rounded-xl" />)}
            </div>
          ) : notifications.length > 0 ? (
            <div className="space-y-4 max-h-[500px] overflow-y-auto">
              {notifications.map((notif) => (
                <div key={notif.id} className="p-4 bg-[#F5E6D3]/30 rounded-xl" data-testid={`notification-${notif.id}`}>
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-medium text-[#4A154B]">{notif.title}</h4>
                    <span className="text-xs text-gray-500" title={format(new Date(notif.created_at), 'PPpp')}>
                      {formatDistanceToNow(new Date(notif.created_at), { addSuffix: true })}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">{notif.message}</p>
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {notif.recipients_count} recipients
                    </span>
                    <span>
                      Target: {notif.target_tiers.includes('all') ? 'All' : notif.target_tiers.join(', ')}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Bell className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No notifications sent yet</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminNotifications;
